import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { HttpService } from '../../services/http.service';
import { StorageService } from '../../services/storage.service';
import { AccountDataDTO, UserAccountDTO } from '../../classes/user-account.dto';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { CommonModule } from '@angular/common';
import { format } from 'date-fns';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ChangePasswordComponent } from '../utility/change-password/change-password.component';
import { ToastrService } from 'ngx-toastr';
import { DataService } from '../../services/data.service';
import { Subject, takeUntil } from 'rxjs';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Router, RouterLink } from '@angular/router';
import { ConfigService } from '../../services/env-config.service';

@Component({
    selector: 'app-your-data',
    imports: [CommonModule, NzIconModule, TranslateModule, NzDividerModule, NzCollapseModule, CommonModule, ChangePasswordComponent, NzTooltipModule, RouterLink],
    templateUrl: './your-data.component.html',
    styleUrls: ['./your-data.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class YourDataComponent implements OnInit, OnDestroy {
  profileDataKeys: Array<string> = [];
  profileData?: AccountDataDTO;
  profileDataModeled: any;
  editingMode: boolean = false;
  formData: FormGroup = new FormGroup({});
  isSecondProfile: boolean = false;
  policyUrl: string = '';
  private destroy$ = new Subject<void>();
  user: any;
  disabledMFA?: boolean;

  constructor(
    private http: HttpService,
    private storage: StorageService,
    private fb: FormBuilder,
    private toast: ToastrService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private dataService: DataService,
    private router: Router,
    private configService: ConfigService
  ) {}

  ngOnInit() {
    this.user = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    this.dataService
      .getAccountData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((profileData) => {
        if (profileData) {
          this.mapAccountData(profileData);
        } else {
          this.getAccountData();
        }
      });
       this.configService.getConfig().subscribe((res: any) =>{
        this.disabledMFA  = res?.disabledMFA;
      });
    this.policyUrl = JSON.parse(this.storage.getItem('userData')!)?.policyUrl;
  }

  private listenForm(): void {
    this.formData.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.cdr.detectChanges();
    });
  }

  private getAccountData(): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    this.http
      .getAccountdDataNoConnectionValidations(userData?.individualId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountData: AccountDataDTO) => {
        this.dataService.setAccountData(accountData);
        this.dataService.setAccountDataFrom(false);
        this.mapAccountData(accountData);
      });
  }

  private mapAccountData(accountData: AccountDataDTO): void {
    this.profileData = accountData;
    const customer = this.profileData.customer;
    if (customer) {
      this.profileDataModeled = {
        name: { value: customer.name + ' ' + customer.surname },
        birthdate: { value: format(new Date(customer.birthDate), 'dd/MM/yyyy') },
        taxcode: { value: customer.taxId },
        telephone: { value: customer.phone, isEditable: true, isPhoneCertified: customer.isPhoneCertified },
        email: { value: customer.email, isEditable: true },
        username: { value: customer.userName },
        address: { value: customer.address },
        postal_code: { value: customer.postalCode },
        city: { value: customer?.city?.description },
      };
    }
    this.dataService.getAccountDataFrom().subscribe((res) => {
      this.isSecondProfile = res;
    });
    this.isSecondProfile = JSON.parse(<string>this.storage.getItem('oldUserData')) ? true : this.isSecondProfile;
    if (this.isSecondProfile) {
      this.dataService.setAccountDataFrom(true);
    }
    this.profileDataKeys = Object.keys(this.profileDataModeled);
    this.createForm();
  }

  private createForm(): void {
    this.formData = this.fb.group({
      telephone: [this.profileDataModeled.telephone.value, [Validators.required, Validators.pattern(/^(?:\+?[0-9]{8,})$/)]],
      email: [this.profileDataModeled.email.value, [Validators.required, Validators.email]],
    });
    this.formData.disable({ emitEvent: false });
    this.listenForm();
  }

  protected enableEditing(): void {
    this.formData.enable({ emitEvent: false });
    this.editingMode = true;
  }

  protected cancelEditing(): void {
    this.formData.disable({ emitEvent: false });
    this.editingMode = false;
  }

  protected findControl(id: string): FormControl | null {
    return this.formData.get(id) as FormControl;
  }

  protected onSave(): void {
    if (this.profileData && this.profileData.customer) {
      const payload = {
        ...this.profileData.customer,
      };
      payload.email = this.formData.get('email')?.value;
      payload.phone = this.formData.get('telephone')?.value;

      this.http
        .updateAccount(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe((_) => {
          this.editingMode = false;
          this.getAccountData();
          const toastText = this.translate.instant('personalArea.myData.edit_successful');
          this.toast.success(toastText);
        });
    }
  }

  protected onOtherDetails(): void {
    window.open(this.policyUrl, '_blank', 'noopener');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public changeUser(): void {
    const oldUserData = JSON.parse(<string>this.storage.getItem('oldUserData')) as UserAccountDTO;
    if (!oldUserData?.individualId) {
      return;
    }

    this.storage.removeItem('oldUserData');
    this.storage.setItem('userData', oldUserData);
    this.dataService.setAccountDataFrom(false);
    this.http
      .getAccountdDataNoConnectionValidations(oldUserData.individualId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountData: AccountDataDTO) => {
        this.dataService.setAccountData(accountData);
        this.mapAccountData(accountData);
        this.router.navigate(['/personal-area/your-data']);
      });
  }
}
